import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/common/AppLayout';
import Button from '../components/common/Button';
import { toast } from 'react-toastify';

export default function ProfilePage() {
  const { user, updatePassword } = useAuth();

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  function formatRole(role) {
    if (!role) return '';
    return role
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  function formatDate(dateString) {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-MY', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  function resetForm() {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
    setShowPasswordForm(false);
  }

  async function handlePasswordChange(e) {
    e.preventDefault();
    const newErrors = {};

    if (!currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await updatePassword(currentPassword, newPassword);
      toast.success('Password updated successfully');
      resetForm();
    } catch (error) {
      if (error.message?.includes('incorrect') || error.message?.includes('Invalid')) {
        setErrors({ currentPassword: 'Current password is incorrect' });
      } else {
        toast.error(error.message || 'Failed to update password');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <AppLayout>
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-display font-bold text-tenq-black">My Profile</h1>
        <p className="text-gray-600">View your account information</p>
      </div>

      {/* Account Information Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <h2 className="text-xl font-display font-bold text-tenq-black mb-6">
          Account Information
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Full Name</label>
            <p className="text-lg font-medium text-tenq-black">{user?.fullName || '-'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Nickname</label>
            <p className="text-lg font-medium text-tenq-black">{user?.nickname || '-'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
            <p className="text-lg text-gray-700">{user?.email || '-'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Role</label>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-tenq-gold/10 text-tenq-gold">
              {formatRole(user?.role)}
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Member Since</label>
            <p className="text-gray-700">{formatDate(user?.startDate)}</p>
          </div>
        </div>
      </div>

      {/* Team Information Card */}
      {user?.leader && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-xl font-display font-bold text-tenq-black mb-4">
            Team Information
          </h2>

          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="w-12 h-12 rounded-full bg-tenq-gold flex items-center justify-center flex-shrink-0">
              <span className="text-lg font-bold text-white">
                {user.leader.fullName?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>

            <div>
              <p className="text-sm text-gray-500">Reports to</p>
              <p className="font-medium text-tenq-black">{user.leader.fullName}</p>
              <p className="text-sm text-gray-500">{user.leader.email}</p>
            </div>
          </div>
        </div>
      )}

      {/* Security Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-display font-bold text-tenq-black">
            Security
          </h2>
          {!showPasswordForm && (
            <Button
              variant="outline-gold"
              size="sm"
              onClick={() => setShowPasswordForm(true)}
            >
              Change Password
            </Button>
          )}
        </div>

        {showPasswordForm ? (
          <form onSubmit={handlePasswordChange} className="space-y-4 pt-4 border-t border-gray-100">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Password
              </label>
              <input
                type="password"
                className={`w-full px-4 py-2.5 rounded-lg border ${
                  errors.currentPassword ? 'border-red-500' : 'border-gray-300'
                } focus:outline-none focus:ring-2 focus:ring-tenq-gold/50 focus:border-tenq-gold`}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
              />
              {errors.currentPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.currentPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                className={`w-full px-4 py-2.5 rounded-lg border ${
                  errors.newPassword ? 'border-red-500' : 'border-gray-300'
                } focus:outline-none focus:ring-2 focus:ring-tenq-gold/50 focus:border-tenq-gold`}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
              <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
              {errors.newPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.newPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                className={`w-full px-4 py-2.5 rounded-lg border ${
                  errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                } focus:outline-none focus:ring-2 focus:ring-tenq-gold/50 focus:border-tenq-gold`}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="secondary"
                type="button"
                onClick={resetForm}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={loading}
              >
                Update Password
              </Button>
            </div>
          </form>
        ) : (
          <p className="text-gray-600">
            Click "Change Password" to update your account password.
          </p>
        )}
      </div>
    </AppLayout>
  );
}
